import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { makePrioStyles } from '../../../theme/utils';
import Flex from '../../../components/Flex';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../../../theme/types';
import { Button, Select } from '@prio365/prio365-react-library';
import { useDispatch, useSelector } from 'react-redux';
import {
  RootReducerState,
  getPhoneLinePreference,
  getSettingsByKey,
  getUserMe,
} from '../../../apps/main/rootReducer';
import { PhoneLinePreference } from '../../../models/Types';
import { useQuery } from '@tanstack/react-query';
import { Setting, SettingKey, UpdateSetting } from '../../../models/Settings';
import {
  createUserSetting,
  updateUserSetting,
} from '../../settings/actions/setting';
import { apiFetchSettings } from '../../settings/api';
import { openIndividualAbsenceNoteDrawer } from '../../absences/actions/individualAbsenceNoteDrawer';
import useIndividualAbsenceNote from '../../absences/hooks/useIndividualAbsenceNote';
import { sanitizeHTML } from '../../../util';

const useStyles = makePrioStyles((theme) => ({
  root: {
    color: theme.colors.application.typography.default,
    padding: theme.spacing.extraLarge,
    background: theme.colors.application.background.light,
    height: '100%',
    overflow: 'auto',
  },
  whiteBox: {
    background: theme.colors.base.white.default,
    padding: theme.spacing.large,
  },
  wrapper: {
    background: theme.colors.application.background.light,
    padding: theme.spacing.large,
  },

  h1: {
    fontSize: theme.font.fontSize.large,
    fontWeight: theme.font.fontWeight.bold,
  },
  h2: {
    fontSize: theme.font.fontSize.regular,
    fontWeight: theme.font.fontWeight.bold,
  },
  label: {
    fontSize: theme.font.fontSize.extraSmall,
    color: theme.colors.application.typography.muted,
  },
  previewCard: {
    background: theme.colors.base.white.default,
    borderRadius: theme.borderRadius.small,
    padding: theme.spacing.regular,
  },
  previewLabel: {
    fontSize: theme.font.fontSize.extraSmall,
    color: theme.colors.application.typography.muted,
    marginBottom: theme.spacing.small,
  },
  previewContent: {
    fontSize: theme.font.fontSize.small,
    color: theme.colors.application.typography.default,
    '& p': {
      margin: 0,
    },
  },
}));

interface PersonalSettingsPageProps {
  className?: string;
}

export const PersonalSettingsPage: React.FC<PersonalSettingsPageProps> = (
  props
) => {
  //#region ------------------------------ Defaults
  const { className } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme<PrioTheme>();
  const dispatch = useDispatch();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const userMe = useSelector(getUserMe);
  const phoneLinePreference = useSelector(getPhoneLinePreference);

  const { hasAbsenceNote, absenceNote } = useIndividualAbsenceNote();

  const phoneLinePreferenceSettings = useSelector<RootReducerState, Setting[]>(
    (state) =>
      getSettingsByKey(state, 'General_PhoneLinePreference').filter(
        (setting) =>
          setting.settingId !== '00000000-0000-0000-0000-000000000000'
      )
  );

  const officePhoneLinePreferenceSettingsKey = useMemo(
    () =>
      [
        {
          scopeId: userMe.myOfficeId,
          eSettingScope: 'office',
          keys: ['General_PhoneLinePreference'],
        },
      ] as SettingKey[],
    [userMe.myOfficeId]
  );

  const { data: officePhoneLinePreferenceApiResult } = useQuery({
    queryKey: ['officePhoneLinePreference'],
    queryFn: () => apiFetchSettings(officePhoneLinePreferenceSettingsKey),
    staleTime: 1000 * 60 * 60, // 1 hour
  });
  const officePhoneLinePreferenceResult = JSON.parse(
    officePhoneLinePreferenceApiResult?.data?.[0].jsonValue ?? '{}'
  );

  const phoneLinePreferenceOptionKeys = Object.keys(
    officePhoneLinePreferenceResult
  );

  const phoneLinePreferenceByOffice = Object.entries(
    officePhoneLinePreferenceResult
  ).find(([_, value]) => value === true)?.[0];

  const phoneLinePreferenceOptions = phoneLinePreferenceOptionKeys.map(
    (item) => ({
      value: item,
      label: t(`profile:personalSettings.phoneLinePreference.${item}`, {
        isDefaultByOffice:
          item === phoneLinePreferenceByOffice
            ? ` ${t('profile:personalSettings.phoneLinePreference.default')}`
            : '',
      }),
    })
  );
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const handlePhoneLinePreferenceChange = useCallback(
    async (value: PhoneLinePreference) => {
      const jsonValue =
        value === 'preferTeams'
          ? {
              preferTeams: true,
              preferPhone: false,
            }
          : {
              preferTeams: false,
              preferPhone: true,
            };
      const updatedSettings = {
        jsonValue: JSON.stringify(jsonValue),
        parentSettingId: '',
        relationId: '',
        isDefault: true,
        description: '',
      } as UpdateSetting;

      if (phoneLinePreferenceSettings.length === 0) {
        dispatch(
          createUserSetting(
            updatedSettings,
            userMe.id,
            'General_PhoneLinePreference'
          )
        );
      } else {
        dispatch(
          updateUserSetting(
            updatedSettings,
            phoneLinePreferenceSettings[0].settingId
          )
        );
      }
    },

    [userMe.id, dispatch, phoneLinePreferenceSettings]
  );

  const handleOpenAbsenceNoteDrawer = useCallback(() => {
    dispatch(openIndividualAbsenceNoteDrawer());
  }, [dispatch]);

  //#endregion

  //#region ------------------------------ Effects
  //#endregion

  return (
    <Flex.Column className={classNames(classes.root, className)}>
      <Flex.Row className={classes.whiteBox}>
        <Flex.Column width={'100%'} childrenGap={theme.spacing.regular}>
          <div className={classes.h1}>
            {t('profile:personalSettings.header')}
          </div>
          <Flex.Row>
            <Flex.Column className={classes.wrapper} width={'100%'}>
              <div className={classes.h2}>
                {t('profile:personalSettings.general.header')}
              </div>
              <Flex.Row childrenGap={theme.spacing.regular}>
                <Flex.Column flex={1}>
                  <div className={classes.label}>
                    {t('profile:personalSettings.general.phoneLinePreference')}
                  </div>
                  <Select
                    showSearch={false}
                    value={phoneLinePreference ?? phoneLinePreferenceByOffice}
                    options={phoneLinePreferenceOptions}
                    onChange={handlePhoneLinePreferenceChange}
                  />
                </Flex.Column>
                <Flex.Column flex={1}></Flex.Column>
              </Flex.Row>
            </Flex.Column>
          </Flex.Row>
          <Flex.Row>
            <Flex.Column className={classes.wrapper} width={'100%'}>
              <div className={classes.h2}>
                {t('profile:personalSettings.individualAbsenceNote.header')}
              </div>
              {hasAbsenceNote ? (
                <Flex.Column
                  childrenGap={theme.spacing.regular}
                  padding={`${theme.spacing.regular}px 0`}
                >
                  {absenceNote.internalNote && (
                    <Flex.Column>
                      <div className={classes.previewLabel}>
                        {t('absences:individualAbsenceNote.internalNote')}
                      </div>
                      <div
                        className={classNames(
                          classes.previewCard,
                          classes.previewContent
                        )}
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHTML(absenceNote.internalNote),
                        }}
                      />
                    </Flex.Column>
                  )}
                  {absenceNote.externalNote && (
                    <Flex.Column>
                      <div className={classes.previewLabel}>
                        {t('absences:individualAbsenceNote.externalNote')}
                      </div>
                      <div
                        className={classNames(
                          classes.previewCard,
                          classes.previewContent
                        )}
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHTML(absenceNote.externalNote),
                        }}
                      />
                    </Flex.Column>
                  )}
                  <Flex.Row justifyContent="center">
                    <Button
                      type="link"
                      onClick={handleOpenAbsenceNoteDrawer}
                      iconProp={['fal', 'pen']}
                    >
                      {t(
                        'profile:personalSettings.individualAbsenceNote.editButton'
                      )}
                    </Button>
                  </Flex.Row>
                </Flex.Column>
              ) : (
                <Flex.Row
                  justifyContent="center"
                  padding={`${theme.spacing.regular}px 0`}
                >
                  <Button
                    type="link"
                    onClick={handleOpenAbsenceNoteDrawer}
                    iconProp={['fal', 'plus']}
                  >
                    {t(
                      'profile:personalSettings.individualAbsenceNote.addButton'
                    )}
                  </Button>
                </Flex.Row>
              )}
            </Flex.Column>
          </Flex.Row>
        </Flex.Column>
      </Flex.Row>
    </Flex.Column>
  );
};

export default PersonalSettingsPage;
